import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import { getPresetDateRangeParams } from '../../../../utils/measurementUtils';
import { PATIENT_SCHEDULE_TARGET_PREF_EVENT } from '../../../../utils/patientScheduleTargetPreference';
import './MeanChart.css';

const MeanChart = ({ practiceId, patientId }) => {
  const navigate = useNavigate();
  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      fetchPatientData();
      fetchScheduleTargets();
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  // Fetch measurements when query parameters change
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId, dateRange, fromDate, toDate]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        if (fromDate && toDate) {
          const [fromMonth, fromDay, fromYear] = fromDate.split('/');
          const [toMonth, toDay, toYear] = toDate.split('/');
          fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
          toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
        }
      }

      if (fromDateParam && toDateParam) {
        const response = await measurementService.getBloodGlucoseByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          'daily_average'
        );

        if (response.data?.success) {
          setMeasurements(response.data.data || []);
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQuery = () => {
    fetchMeasurements();
  };

  const patientName = patientData?.patient 
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth 
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (!scheduleTargets?.bloodGlucoseTimePeriod) {
      return ['Wake-up Time', 'Before-Breakfast', 'After-Breakfast', 'Before-Lunch', 'After-Lunch', 'Before-Dinner', 'After-Dinner', 'Bedtime', 'Midnight'];
    }
    return scheduleTargets.bloodGlucoseTimePeriod.map(p => p.name);
  }, [scheduleTargets]);

  // Determine period from measurement
  const determinePeriodFromMeasurement = (measurement) => {
    const period =
      measurement.period_name ||
      measurement.period ||
      measurement.measure_note ||
      measurement.note ||
      '';
    const periodLower = period.toLowerCase();
    if (periodLower.includes('wake') || periodLower.includes('wake-up')) return 'Wake-up Time';
    if (periodLower.includes('before') && periodLower.includes('breakfast')) return 'Before-Breakfast';
    if (periodLower.includes('after') && periodLower.includes('breakfast')) return 'After-Breakfast';
    if (periodLower.includes('before') && periodLower.includes('lunch')) return 'Before-Lunch';
    if (periodLower.includes('after') && periodLower.includes('lunch')) return 'After-Lunch';
    if (periodLower.includes('before') && periodLower.includes('dinner')) return 'Before-Dinner';
    if (periodLower.includes('after') && periodLower.includes('dinner')) return 'After-Dinner';
    if (periodLower.includes('bedtime') || periodLower.includes('bed')) return 'Bedtime';
    if (periodLower.includes('midnight')) return 'Midnight';
    return null;
  };

  // Calculate mean values by period
  const periodMeans = useMemo(() => {
    if (!measurements || measurements.length === 0) {
      return periodNames.map(name => ({ name, mean: null }));
    }

    return periodNames.map(periodName => {
      const periodMeasurements = measurements.filter(m => {
        const determinedPeriod = determinePeriodFromMeasurement(m);
        return determinedPeriod === periodName;
      });

      if (periodMeasurements.length === 0) {
        return { name: periodName, mean: null };
      }

      const bgValues = periodMeasurements
        .map(m => parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value))
        .filter(v => !isNaN(v));

      const mean = bgValues.length > 0 
        ? bgValues.reduce((a, b) => a + b, 0) / bgValues.length 
        : null;

      return { name: periodName, mean };
    });
  }, [measurements, periodNames]);

  // Chart dimensions and scaling
  const chartWidth = 800;
  const chartHeight = 250;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 400; // mg/dL
  const minY = 0;

  // Scale functions
  const scaleX = (index) => {
    if (periodNames.length <= 1) return padding.left;
    return padding.left + (index / (periodNames.length - 1)) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  return (
    <div className="bg-mean-chart-page">
      {/* Header Section */}
      <div className="bgmc-header">
        <button className="bgmc-nav-button active">Blood Glucose Chart</button>
        <div className="bgmc-patient-info">
          <div className="bgmc-info-row">
            <span className="bgmc-label">Name :</span>
            <span className="bgmc-value">{patientName}</span>
          </div>
          <div className="bgmc-info-row">
            <span className="bgmc-label">DOB :</span>
            <span className="bgmc-value">{patientDOB}</span>
          </div>
        </div>
      </div>

      {/* Query Period Section */}
      <div className="bgmc-query-section">
        <h4>Query Period</h4>
        <div className="bgmc-radio-group">
          <label className="bgmc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="dateRange"
              checked={queryType === 'dateRange'}
              onChange={(e) => setQueryType(e.target.value)}
            />
            <span>Date Range</span>
            {queryType === 'dateRange' && (
              <select 
                className="bgmc-dropdown"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option>Last 30 days</option>
                <option>Last 7 days</option>
                <option>Last 14 days</option>
                <option>Last 60 days</option>
                <option>Last 90 days</option>
              </select>
            )}
          </label>
          <label className="bgmc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="from"
              checked={queryType === 'from'}
              onChange={(e) => setQueryType(e.target.value)}
            />
            <span>From</span>
            {queryType === 'from' && (
              <div className="bgmc-date-inputs">
                <div className="bgmc-date-wrapper">
                  <input
                    type="text"
                    className="bgmc-date-input"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    placeholder="mm/dd/yyyy"
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="bgmc-calendar-icon" />
                </div>
                <span className="bgmc-date-separator">to</span>
                <div className="bgmc-date-wrapper">
                  <input
                    type="text"
                    className="bgmc-date-input"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    placeholder="mm/dd/yyyy"
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="bgmc-calendar-icon" />
                </div>
              </div>
            )}
          </label>
        </div>
        <div className="bgmc-action-buttons">
          <button className="bgmc-action-btn" onClick={handleQuery} disabled={queryLoading}>
            {queryLoading ? 'Loading...' : 'Query'}
          </button>
          <button 
            className="bgmc-action-btn"
            onClick={() => navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/chart`)}
          >
            Day Chart
          </button>
          <button className="bgmc-action-btn">Print</button>
          <button className="bgmc-action-btn">Export</button>
          <button className="bgmc-action-btn">Email</button>
        </div>
      </div>

      {/* Mean Chart Section */}
      <div className="bgmc-chart-section">
        <div className="bgmc-chart-title-top-left">Mean Chart of Blood Glucose</div>
        <div className="bgmc-chart-title-center">Measurement Period Mean Chart of Blood Glucose</div>
        <div className="bgmc-chart-container">
          <div className="bgmc-chart-legend">
            <div className="bgmc-legend-item">
              <div className="bgmc-legend-dot bgmc-legend-glucose"></div>
              <span>Glucose</span>
            </div>
          </div>
          <div className="bgmc-chart-area">
            {loading || queryLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading chart data...</div>
            ) : periodMeans.every(p => p.mean == null) ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
            ) : (
              <div className="bgmc-chart-placeholder">
                <div className="bgmc-y-axis">
                  {[400, 350, 300, 250, 200, 150, 100, 50, 0].map(value => (
                    <span key={value}>{value}</span>
                  ))}
                </div>
                <div className="bgmc-chart-grid">
                  <svg width={chartWidth} height={chartHeight} style={{ position: 'absolute', top: 0, left: 0 }}>
                    {/* Grid lines */}
                    {[0, 50, 100, 150, 200, 250, 300, 350, 400].map(value => {
                      const y = scaleY(value);
                      if (y == null) return null;
                      return (
                        <line
                          key={value}
                          x1={padding.left}
                          y1={y}
                          x2={chartWidth - padding.right}
                          y2={y}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                        />
                      );
                    })}
                    {/* Vertical grid lines for periods */}
                    {periodNames.map((periodName, index) => {
                      const x = scaleX(index);
                      return (
                        <line
                          key={periodName}
                          x1={x}
                          y1={padding.top}
                          x2={x}
                          y2={chartHeight - padding.bottom}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                          strokeDasharray="2,2"
                        />
                      );
                    })}
                    {/* Glucose dots (blue) */}
                    {periodMeans.map((period, index) => {
                      if (period.mean == null) return null;
                      const x = scaleX(index);
                      const y = scaleY(period.mean);
                      if (y == null) return null;
                      return (
                        <circle
                          key={`glucose-${period.name}`}
                          cx={x}
                          cy={y}
                          r="5"
                          fill="#1976d2"
                        />
                      );
                    })}
                  </svg>
                  <div className="bgmc-x-axis">
                    {periodNames.map(periodName => (
                      <span key={periodName}>{periodName}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="bgmc-y-axis-label">Blood Glucose mg/dL</div>
        </div>
      </div>
    </div>
  );
};

export default MeanChart;

